import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandT } from '@sorare/core/src/atoms/typography';
import { isType, withFragments } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import type {
  CareerLineupsBoardSwitcher_careerBoard,
  boardForTask_careerBoard,
  boardsWithCareerProSteps_careerBoard,
  careerProSteps_careerBoard,
} from './__generated__/index.graphql.ts';

const BoardIcon = styled.img`
  width: calc(3 * var(--unit));
  height: calc(3 * var(--unit));
  border-radius: var(--half-unit);
  object-fit: contain;
`;

const GridTitle = styled(BrandT)`
  text-align: center;
  padding: var(--unit);
`;

const Grid = styled(Horizontal).attrs({ gap: 1, center: true })`
  flex-wrap: wrap;
  padding: var(--double-unit);

  @media ${laptopAndAbove} {
    max-width: calc(45 * var(--unit));
  }
`;

const GridCard = styled(ButtonBase)`
  position: relative;
  width: calc(33% - var(--unit));
  aspect-ratio: 1;
  border-radius: var(--radius-l);
  background: linear-gradient(
      rgba(var(--c-rgb-black), 0.6),
      rgba(var(--c-rgb-black), 0.4)
    )
    var(--board-color, var(--c-nd-100));
  padding: var(--double-unit);
  display: flex;
  align-items: center;
  justify-content: center;

  &.selected {
    border: 2px solid var(--c-white);
  }
`;

const GridCardImage = styled.img`
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
  object-fit: contain;
`;

const ClaimableBadge = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: absolute;
  top: var(--unit);
  right: var(--unit);
  width: calc(var(--double-unit));
  height: calc(var(--double-unit));
  color: var(--c-green-400);
`;

const StyledButtonBase = styled(ButtonBase)`
  padding: var(--intermediate-unit);
  background: var(--c-nd-100-opaque);
  border-radius: 50%;
`;

type Props = {
  boards: readonly CareerLineupsBoardSwitcher_careerBoard[];
  currentTaskId: string;
  onSelect: (params: { boardId: string; taskId: string }) => void;
};

const careerProSteps = withFragments(
  <T extends careerProSteps_careerBoard>(
    board: T
  ): Extract<T['steps'][number], { __typename: 'CareerProStep' }>[] =>
    board.steps.filter(
      (
        s
      ): s is Extract<T['steps'][number], { __typename: 'CareerProStep' }> => {
        if (!isType(s, 'CareerProStep')) {
          return false;
        }
        return !!s.myTask?.id;
      }
    ),
  {
    careerBoard: gql`
      fragment careerProSteps_careerBoard on CareerBoard {
        id
        steps {
          id
          ... on CareerProStep {
            id
            myTask {
              id
              aasmState
            }
          }
        }
      }
    ` as TypedDocumentNode<careerProSteps_careerBoard>,
  }
);

export const boardsWithCareerProSteps = withFragments(
  <T extends boardsWithCareerProSteps_careerBoard>(boards: readonly T[]) =>
    boards.filter((b): b is T => careerProSteps(b).length > 0),
  {
    careerBoard: gql`
      fragment boardsWithCareerProSteps_careerBoard on CareerBoard {
        id
        ...careerProSteps_careerBoard
      }
      ${careerProSteps.fragments.careerBoard}
    ` as TypedDocumentNode<boardsWithCareerProSteps_careerBoard>,
  }
);

export const boardForTask = withFragments(
  <T extends boardForTask_careerBoard>(boards: readonly T[], taskId: string) =>
    boards.find(b => careerProSteps(b).some(s => s.myTask.id === taskId)),
  {
    careerBoard: gql`
      fragment boardForTask_careerBoard on CareerBoard {
        id
        ...careerProSteps_careerBoard
      }
      ${careerProSteps.fragments.careerBoard}
    ` as TypedDocumentNode<boardForTask_careerBoard>,
  }
);

const hasClaimableTask = (board: CareerLineupsBoardSwitcher_careerBoard) =>
  careerProSteps(board).some(
    s => s.myTask.aasmState === ManagerProgressionTaskState.COMPLETED
  );

export const CareerLineupsBoardSwitcher = ({
  boards,
  currentTaskId,
  onSelect,
}: Props) => {
  const boardsWithSteps = boardsWithCareerProSteps(boards);
  const currentBoard = boardForTask(boardsWithSteps, currentTaskId);

  if (boardsWithSteps.length <= 1) {
    return null;
  }

  const handleSelectBoard = (boardId: string, closeDropdown: () => void) => {
    const board = boardsWithSteps.find(b => b.id === boardId);
    if (!board) return;

    const steps = careerProSteps(board);
    const taskId = steps.find(s => {
      const state = s.myTask.aasmState;
      return (
        state === ManagerProgressionTaskState.READY ||
        state === ManagerProgressionTaskState.COMPLETED
      );
    })?.myTask.id;
    if (!taskId) return;

    onSelect({ boardId: board.id, taskId });
    closeDropdown();
  };

  return (
    <Dropdown
      align="center"
      gap={8}
      label={props => (
        <StyledButtonBase
          type="button"
          aria-label={currentBoard?.title ?? undefined}
          {...props}
        >
          {currentBoard?.iconUrl ? (
            <BoardIcon src={currentBoard.iconUrl} alt="" role="presentation" />
          ) : null}
        </StyledButtonBase>
      )}
    >
      {({ closeDropdown }) => (
        <>
          <GridTitle>
            <FormattedMessage defaultMessage="Choose League" />
          </GridTitle>
          <Grid>
            {[...boardsWithSteps].reverse().map(board => (
              <GridCard
                key={board.id}
                type="button"
                className={classNames({
                  selected: board.id === currentBoard?.id,
                })}
                style={
                  board.color
                    ? ({ '--board-color': board.color } as React.CSSProperties)
                    : undefined
                }
                onClick={() => handleSelectBoard(board.id, closeDropdown)}
              >
                {!!board.iconUrl && (
                  <GridCardImage src={board.iconUrl} alt={board.title ?? ''} />
                )}
                {!!hasClaimableTask(board) && (
                  <ClaimableBadge>
                    <FontAwesomeIcon icon={iconGift} size="xs" />
                  </ClaimableBadge>
                )}
              </GridCard>
            ))}
          </Grid>
        </>
      )}
    </Dropdown>
  );
};

CareerLineupsBoardSwitcher.fragments = {
  careerBoard: gql`
    fragment CareerLineupsBoardSwitcher_careerBoard on CareerBoard {
      id
      title
      iconUrl
      color(variant: PRIMARY)
      ...boardsWithCareerProSteps_careerBoard
      ...boardForTask_careerBoard
      steps {
        id
        ... on CareerProStep {
          id
          myTask {
            id
          }
        }
      }
    }
    ${boardsWithCareerProSteps.fragments.careerBoard}
    ${boardForTask.fragments.careerBoard}
  ` as TypedDocumentNode<CareerLineupsBoardSwitcher_careerBoard>,
};
